import { useFlattenedGeometryShapes, usePortsGetList } from '@ais-ui/api';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Label,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  useStepperReturn,
} from '@ais-ui/ui-core';
import React, { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { StepperFooter } from './StepperFooter';
import { ShapeSelect } from '../../../Shapes/ShapeSelects/ShapeSelect';

import { ReportShapeItem, ShapeStructure, ShapeType } from '../../reportsTypes';
import { ShapeMultiSelect } from '../../../Shapes/ShapeSelects/ShapeMultiSelect';
import { createShapeDictionary } from '../utils/createShapeDictionary';
import { StepperHeadline } from './StepperHeadline';
import { useLocalization } from '@ais-ui/locale-management';
import { usePermissions } from '../../../Permissions/PermissionsProvider';
import { DrawPolygonField } from '../../../Shapes/DrawFormFields/DrawPolygonField';
import { DrawLineField } from '../../../Shapes/DrawFormFields/DrawLineField';
import { DrawPointField } from '../../../Shapes/DrawFormFields/DrawPointField';
import { DrawCircleField } from '../../../Shapes/DrawFormFields/DrawCircleField';
import { useCreateReportContext } from './CreateReportContext';
import { ReportTemplateShapeValues, useFormSetup } from './useDynamicShapeForm';
import { PortMultiSelect } from '../../../Shapes/ShapeSelects/PortMultiSelect';
import { FormFieldTypes } from '../utils/formFieldTypes';
import { useDrawWithViewMode } from '../../../../hooks/useDrawWithViewMode';

interface AdHocShapeSelectProps {
  allowedGeometryTypes: string[];
  geometryField: string;
  form: UseFormReturn<any>;
  shouldAutoTrigger: boolean;
  onAutoTriggerComplete: () => void;
}

const AdHocShapeSelect = ({
  allowedGeometryTypes,
  geometryField,
  form,
  shouldAutoTrigger,
  onAutoTriggerComplete,
}: AdHocShapeSelectProps) => {
  const [tab, setTab] = useState<string>('polygon');
  const [maxPoints, setMaxPoints] = useState<number | undefined>();
  const { setShowDialog } = useCreateReportContext();

  const { onStartDrawingFromDialog } = useDrawWithViewMode(
    form,
    setShowDialog,
    geometryField,
  );
  const lowerCaseTypes = allowedGeometryTypes.map((type) => type.toLowerCase());

  useEffect(() => {
    if (lowerCaseTypes.length <= 1) {
      if (lowerCaseTypes.includes('polygon')) {
        setTab('polygon');
        setMaxPoints(undefined);
      } else if (lowerCaseTypes.includes('linestring')) {
        setTab('linestring');
        setMaxPoints(undefined);
      } else if (lowerCaseTypes.includes('twopointlinestring')) {
        setTab('linestring');
        setMaxPoints(2);
      } else if (lowerCaseTypes.includes('point')) {
        setTab('point');
        setMaxPoints(undefined);
      } else if (lowerCaseTypes.includes('circle')) {
        setTab('circle');
        setMaxPoints(undefined);
      }
    }
  }, [lowerCaseTypes]);

  // Watch the form field value to get reactive updates
  const geometry = form.watch(geometryField);

  // Auto-trigger drawing when shouldAutoTrigger is true
  useEffect(() => {
    if (shouldAutoTrigger && !geometry) {
      // Trigger drawing based on the current tab
      if (tab === 'polygon') {
        onStartDrawingFromDialog('Polygon', null);
      } else if (tab === 'linestring') {
        onStartDrawingFromDialog('LineString', null);
      } else if (tab === 'point') {
        onStartDrawingFromDialog('Point', null);
      } else if (tab === 'circle') {
        onStartDrawingFromDialog('Circle', null);
      }
      onAutoTriggerComplete();
    }
  }, [
    shouldAutoTrigger,
    geometry,
    tab,
    onStartDrawingFromDialog,
    onAutoTriggerComplete,
  ]);

  return (
    <Tabs
      value={tab}
      onValueChange={setTab}
      defaultValue="polygon"
      className="w-full space-y-4 pt-4 h-full flex flex-col"
    >
      {lowerCaseTypes.length > 1 ? (
        <TabsList className="w-fit">
          {lowerCaseTypes.includes('polygon') && (
            <TabsTrigger value="polygon">Polygon</TabsTrigger>
          )}
          {lowerCaseTypes.includes('linestring') && (
            <TabsTrigger value="linestring">Line</TabsTrigger>
          )}
          {lowerCaseTypes.includes('point') && (
            <TabsTrigger value="point">Point</TabsTrigger>
          )}
          {lowerCaseTypes.includes('circle') && (
            <TabsTrigger value="circle">Circle</TabsTrigger>
          )}
        </TabsList>
      ) : null}
      {tab === 'polygon' && (
        <DrawPolygonField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          color={'blue'}
        ></DrawPolygonField>
      )}
      {tab === 'linestring' && (
        <DrawLineField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          maxPoints={maxPoints}
          color={'blue'}
        ></DrawLineField>
      )}
      {tab === 'point' && (
        <DrawPointField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          color={'blue'}
        ></DrawPointField>
      )}
      {tab === 'circle' && (
        <DrawCircleField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          color={'blue'}
        ></DrawCircleField>
      )}
    </Tabs>
  );
};

interface ShapeFormProps {
  shapeItems: ReportShapeItem[];
  stepperProps: useStepperReturn;
}

interface MultiShapeFormProps {
  shapeItems: ReportShapeItem[];
  stepperProps: useStepperReturn;
}

interface PortFormProps {
  shapeItems: ReportShapeItem[];
  stepperProps: useStepperReturn;
}

interface WorldShapeFormProps {
  shapeItems: ReportShapeItem[];
  stepperProps: useStepperReturn;
}

interface SingleContentProps {
  shapeFields: ShapeStructure;
  stepperProps: useStepperReturn;
}

interface TabbedContentProps {
  shapeFields: ShapeStructure;
  stepperProps: useStepperReturn;
}

const ShapeLabel = ({ shapeItem }: { shapeItem: ReportShapeItem }) => {
  return (
    <div className="flex justify-between">
      <FormLabel>{shapeItem.label}: *</FormLabel>
      {shapeItem.help && (
        <InfoIconTooltip className="mr-2">{shapeItem.help}</InfoIconTooltip>
      )}
    </div>
  );
};

const ShapeForm = ({ shapeItems, stepperProps }: ShapeFormProps) => {
  const {
    shapeDictionary,
    setShapeDictionary,
    instantShapeData,
    instantShape,
    setInstantShape,
  } = useCreateReportContext();

  const { messages } = useLocalization();
  const currentShape: ShapeType = 'shape';
  const previousInstantShapeRef = React.useRef(instantShape);
  const [shouldAutoTrigger, setShouldAutoTrigger] = React.useState(false);

  const form = useFormSetup(
    shapeItems,
    shapeDictionary,
    currentShape,
    instantShape ? instantShapeData : null,
    instantShape,
  );

  const { data: shapes } = useFlattenedGeometryShapes();
  
  // Update form values and shapeDictionary when instantShapeData changes
  React.useEffect(() => {
    if (instantShape && instantShapeData && Object.keys(instantShapeData).length > 0 && shapes) {
      // Set the form field values from instantShapeData
      Object.keys(instantShapeData).forEach((key) => {
        const geometry = instantShapeData[key];
        form.setValue(key, geometry);
      });
      
      // Manually create and set shapeDictionary since form.watch doesn't trigger on setValue
      const newShapeDictionary = createShapeDictionary(
        instantShapeData as any,
        shapes,
        true,
      );
      setShapeDictionary(newShapeDictionary);
    }
  }, [instantShapeData, instantShape, form, shapes, setShapeDictionary]);

  // Track when user explicitly toggles instant shape to true
  React.useEffect(() => {
    if (!previousInstantShapeRef.current && instantShape) {
      // Changed from false to true - user clicked the toggle
      setShouldAutoTrigger(true);
    }
    previousInstantShapeRef.current = instantShape;
  }, [instantShape]);

  // Sync form changes to context in real-time (like quick mode does)
  useEffect(() => {
    if (!form) {
      return;
    }
    
    const subscription = form.watch((values) => {
      if (!shapes || !values) {
        return;
      }

      const newShapeDictionary = createShapeDictionary(
        values as any,
        shapes,
        instantShape,
      );
      setShapeDictionary(newShapeDictionary);
    });

    return () => subscription.unsubscribe();
  }, [form, shapes, instantShape, setShapeDictionary]);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = () => {
    // Shape dictionary is already synced via watch, just move to next step
    stepperProps.nextStep();
  };
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-[100%]"
      >
        <div className="h-[85%]">
          <div className="flex space-x-4">
            {shapeItems.map((shapeItem) => (
              <FormField
                key={shapeItem.name}
                control={form.control}
                name={shapeItem.name}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <div className="flex justify-between items-center mb-2">
                      <ShapeLabel shapeItem={shapeItem} />
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="report-shape-switch"
                          checked={instantShape}
                          onCheckedChange={(value) => {
                            setInstantShape(value);
                          }}
                        />
                        <Label className="text-sm">
                          {messages.reportsCreateReportShapeInstantShape}
                        </Label>
                        <InfoIconTooltip>
                          {messages.reportsCreateReportShapeInstantShapeTooltip}
                        </InfoIconTooltip>
                      </div>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      {!instantShape ? (
                        <ShapeSelect
                          id="shape-select-dropdown"
                          value={FormFieldTypes.asString(field)}
                          onChange={field.onChange}
                          allowedGeometryTypes={shapeItem.allowedGeometryTypes}
                        />
                      ) : (
                        <AdHocShapeSelect
                          allowedGeometryTypes={shapeItem.allowedGeometryTypes}
                          geometryField={shapeItem.name}
                          form={form}
                          shouldAutoTrigger={shouldAutoTrigger}
                          onAutoTriggerComplete={() =>
                            setShouldAutoTrigger(false)
                          }
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

const PortForm = ({ shapeItems, stepperProps }: PortFormProps) => {
  const { shapeDictionary, setShapeDictionary } = useCreateReportContext();

  // This might have to be determined from shapeItems if multiple ports should
  // be supported.
  const shapeItemKey: ShapeType = 'ports';

  const form = useFormSetup(shapeItems, shapeDictionary, shapeItemKey);
  const { data } = usePortsGetList();

  const ports = useMemo(() => data?.records || [], [data]);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = (data: ReportTemplateShapeValues) => {
    if (!ports) {
      return;
    }
    setShapeDictionary(data as any);

    stepperProps.nextStep();
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-full"
      >
        <div className="h-[85%]">
          <div className="space-y-6">
            {shapeItems.map((shapeItem) => (
              <FormField
                key={shapeItem.name}
                control={form.control}
                name={shapeItem.name}
                render={({ field }) => (
                  <FormItem>
                    <ShapeLabel shapeItem={shapeItem} />
                    <FormControl onChange={() => form.clearErrors()}>
                      <PortMultiSelect
                        value={Array.isArray(field.value) ? field.value : []}
                        onChange={field.onChange}
                      ></PortMultiSelect>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

const WorldForm = ({ shapeItems, stepperProps }: WorldShapeFormProps) => {
  const { shapeDictionary, setShapeDictionary } = useCreateReportContext();

  const shapeItemKey: ShapeType = 'worldShape';
  const form = useFormSetup(shapeItems, shapeDictionary, shapeItemKey);
  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = (data: ReportTemplateShapeValues) => {
    setShapeDictionary(data as any);
    stepperProps.nextStep();
  };
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-full"
      >
        <div className="h-[85%]">
          <div className="space-y-6">
            {shapeItems.map((shapeItem) => (
              <FormField
                key={shapeItem.name}
                control={form.control}
                name={shapeItem.name}
                render={({ field }) => (
                  <FormItem className="flex flex-col space-y-4">
                    <ShapeLabel shapeItem={shapeItem} />
                    <FormControl onChange={() => form.clearErrors()}>
                      <Switch
                        disabled={true}
                        checked={FormFieldTypes.asBoolean(field)}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

const MultiShapeForm = ({ shapeItems, stepperProps }: MultiShapeFormProps) => {
  const { shapeDictionary, setShapeDictionary, activeTab } =
    useCreateReportContext();

  const shapeItemKey: ShapeType = 'multiShape';
  const form = useFormSetup(shapeItems, shapeDictionary, shapeItemKey);

  const { data: shapes } = useFlattenedGeometryShapes();

  // Sync form changes to context in real-time (like quick mode does)
  useEffect(() => {
    const subscription = form.watch((values) => {
      if (!shapes || !values) return;

      const newShapeDictionary = createShapeDictionary(values as any, shapes);
      setShapeDictionary(newShapeDictionary);
    });

    return () => subscription.unsubscribe();
  }, [form, shapes, setShapeDictionary]);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = () => {
    if (!shapes) {
      return;
    }

    // Shape dictionary is already synced via watch, just move to next step
    stepperProps.nextStep();
  };
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onNext)}
        className="flex flex-col space-y-8 pt-4 h-full"
      >
        <div className="h-[85%]">
          <div className="space-y-6">
            {shapeItems.map((shapeItem) => (
              <FormField
                key={shapeItem.name}
                control={form.control}
                name={shapeItem.name}
                render={({ field }) => (
                  <FormItem>
                    <ShapeLabel shapeItem={shapeItem} />
                    <FormControl onChange={() => form.clearErrors()}>
                      <ShapeMultiSelect
                        value={FormFieldTypes.asStringArray(field)}
                        onChange={field.onChange}
                        allowedGeometryTypes={shapeItem.allowedGeometryTypes}
                      ></ShapeMultiSelect>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </div>
        <StepperFooter
          onPrevious={onPrevious}
          onNext={form.handleSubmit(onNext)}
          stepperProps={stepperProps}
        ></StepperFooter>
      </form>
    </Form>
  );
};

const SingleContent = ({ shapeFields, stepperProps }: SingleContentProps) => {
  if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'shape') &&
    shapeFields.shape !== undefined
  ) {
    const shapeItems: ReportShapeItem[] = shapeFields.shape;
    return <ShapeForm shapeItems={shapeItems} stepperProps={stepperProps} />;
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'multiShape') &&
    shapeFields.multiShape !== undefined
  ) {
    const shapeItems: ReportShapeItem[] = shapeFields.multiShape;
    return (
      <MultiShapeForm
        stepperProps={stepperProps}
        shapeItems={shapeItems}
      ></MultiShapeForm>
    );
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'worldShape') &&
    shapeFields.worldShape !== undefined
  ) {
    return;
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'ports') &&
    shapeFields.ports !== undefined
  ) {
    return;
  }
};

const TabbedContent = ({ shapeFields, stepperProps }: TabbedContentProps) => {
  const { messages } = useLocalization();
  const { shapeTab, setShapeTab } = useCreateReportContext();
  const permissions = usePermissions();

  const shapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'shape')) {
      return shapeFields.shape;
    }
    return null; // or a default value
  }, [shapeFields]);

  const multiShapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'multiShape')) {
      return shapeFields.multiShape;
    }
    return null; // or a default value
  }, [shapeFields]);

  const portItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'ports')) {
      return shapeFields.ports;
    }
    return null; // or a default value
  }, [shapeFields]);

  const worldShapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'worldShape')) {
      return shapeFields.worldShape;
    }
    return null; // or a default value
  }, [shapeFields]);

  return (
    <Tabs
      value={shapeTab}
      onValueChange={(value) => setShapeTab(value as ShapeType)}
      defaultValue="shape"
      className="w-full space-y-4 pt-4 h-full flex flex-col"
    >
      <TabsList className="w-fit">
        {shapeItems && (
          <TabsTrigger value="shape">
            {messages.reportsCreateReportShapeTabShape}
          </TabsTrigger>
        )}
        {multiShapeItems && (
          <TabsTrigger value="multiShape">
            {messages.reportsCreateReportShapeTabMultiShape}
          </TabsTrigger>
        )}
        {portItems && (
          <TabsTrigger value="ports">
            {messages.reportsCreateReportShapeTabPort}
          </TabsTrigger>
        )}
        {worldShapeItems && (
          <TabsTrigger value="worldShape" disabled={!permissions.useWorldShape}>
            {messages.reportsCreateReportShapeTabWorld}
          </TabsTrigger>
        )}
      </TabsList>
      {shapeItems && shapeTab === 'shape' && (
        <ShapeForm shapeItems={shapeItems} stepperProps={stepperProps} />
      )}
      {multiShapeItems && shapeTab === 'multiShape' && (
        <MultiShapeForm
          shapeItems={multiShapeItems}
          stepperProps={stepperProps}
        />
      )}
      {portItems && shapeTab === 'ports' && (
        <PortForm shapeItems={portItems} stepperProps={stepperProps} />
      )}
      {worldShapeItems && shapeTab === 'worldShape' && (
        <WorldForm shapeItems={worldShapeItems} stepperProps={stepperProps} />
      )}
    </Tabs>
  );
};

export const ShapeStep = () => {
  const { messages } = useLocalization();

  const { stepperProps, chosenReportType } = useCreateReportContext();

  const shapeFields = useMemo(() => {
    return chosenReportType?.shape as ShapeStructure;
  }, [chosenReportType]);

  return (
    <div className="grid grid-rows-[auto_1fr_auto] h-full">
      <StepperHeadline
        headerText={messages.reportsCreateReportShapeTitle}
        subHeaderText={messages.reportsCreateReportShapeSubtitle}
      ></StepperHeadline>
      {shapeFields && Object.keys(shapeFields).length === 1 && (
        <SingleContent
          stepperProps={stepperProps}
          shapeFields={shapeFields}
        ></SingleContent>
      )}
      {shapeFields && Object.keys(shapeFields).length > 1 && (
        <TabbedContent
          stepperProps={stepperProps}
          shapeFields={shapeFields}
        ></TabbedContent>
      )}
    </div>
  );
};
